import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Form, FormField, FormItem, FormControl, FormMessage } from "@/components/ui/form";
import { authService } from "@/services/auth-service";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle, Loader2, ArrowLeft } from 'lucide-react';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { toast } from "sonner";
import { isValidPrefix } from "@/constants";




const formSchema = z.object({
    email: z.string().email("Email invalide"),
    password: z.string().min(6, "Le mot de passe doit contenir au moins 6 caractères"),
    firstName: z.string().nonempty("Veuillez entrer un prénom"),
    lastName: z.string().nonempty("Veuillez entrer un nom"),
    confirmPassword: z.string().min(6, "Le mot de passe doit contenir au moins 6 caractères"),
    phoneNumber: z
        .string()
        .min(8, "Le numéro de téléphone doit contenir 8 chiffres")
        .refine((phone) => isValidPrefix(phone), {
            message: "Le numéro de téléphone doit commencer par un préfixe valide 70 , 74 ...",
        }),
}).refine((data) => data.password === data.confirmPassword, {
    message: "Les mots de passe ne correspondent pas",
    path: ["confirmPassword"],
});

type FormValues = z.infer<typeof formSchema>;

function RegisterPage() {
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const [error, setError] = useState<string | null>(null);

    const form = useForm<FormValues>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            email: "",
            firstName: "",
            lastName: "",
            password: "",
            confirmPassword: "",
            phoneNumber: "",
        },
    });

    const onSubmit = async (data: FormValues) => {
        setIsLoading(true);
        try {
            const registerResponse = await authService.register(data.email, data.password, `226${data.phoneNumber}`, data.firstName, data.lastName);
            if (registerResponse?.success && registerResponse.redirectTo !== undefined) {
                setError(null);

                navigate(registerResponse.redirectTo);
                window.location.reload();
            }
            if (!registerResponse?.success) {
                setError(
                    registerResponse?.error?.message ||
                    "Une erreur est survenue lors de l'inscription"
                );
            }
            if (registerResponse?.success) {
                toast.promise(
                    Promise.resolve(registerResponse),
                    {
                        loading: "Traitement en cours...",
                        success: "Un email de vérification vous a été envoyé. Merci de le consulter pour poursuivre votre inscription",
                        error: "Une erreur s'est produite",
                    }
                );
            }

        } catch (error: unknown) {
            if (error instanceof Error) {
                console.error("Erreur  :", error.message);
            } else {
                console.error("Erreur inconnue :", error);
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="flex min-h-svh w-full items-center justify-center p-6 md:p-10">

            <div className="w-full max-w-sm">
                <div className={cn("flex flex-col gap-6")}>

                    <Card>
                        <CardHeader>
                            <div className="flex items-center gap-2 mb-2">
                                <button
                                    onClick={() => navigate("/")}
                                    className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 transition-colors"
                                >
                                    <ArrowLeft className="w-4 h-4" />
                                    Retour à l'accueil
                                </button>
                            </div>
                            <CardTitle className="items-center justify-center text-center text-2xl">Inscription</CardTitle>
                            <CardDescription className="items-center justify-center text-center ">
                                Entrez vos informations pour vous inscrire
                            </CardDescription>
                        </CardHeader>

                        <CardContent>
                            {error && (
                                <Alert variant="destructive">
                                    <AlertCircle className="h-4 w-4" />
                                    <AlertTitle>Échec d'authentification</AlertTitle>
                                    <AlertDescription>
                                        {error}
                                    </AlertDescription>
                                </Alert>
                            )}
                            <Form {...form}>
                                <form onSubmit={form.handleSubmit(onSubmit)}>
                                    <div className="flex flex-col gap-6">
                                        <div className="grid gap-2">
                                            <div className="flex items-center">
                                                <Label htmlFor="firstName">Prénom(s)</Label>
                                            </div>
                                            <FormField
                                                control={form.control}
                                                name="firstName"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormControl>
                                                            <Input
                                                                id="firstName"
                                                                type="text"
                                                                required
                                                                {...field}
                                                            />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                        </div>
                                        <div className="grid gap-2">
                                            <div className="flex items-center">
                                                <Label htmlFor="lastName">Nom</Label>
                                            </div>
                                            <FormField
                                                control={form.control}
                                                name="lastName"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormControl>
                                                            <Input
                                                                id="lastName"
                                                                type="text"
                                                                required
                                                                {...field}
                                                            />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                        </div>
                                        <div className="grid gap-2">
                                            <div className="flex items-center">
                                                <Label htmlFor="email">Email</Label>
                                            </div>
                                            <FormField
                                                control={form.control}
                                                name="email"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormControl>
                                                            <Input
                                                                id="email"
                                                                type="email"
                                                                placeholder="m@example.com"
                                                                required
                                                                {...field}
                                                            />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                        </div>
                                        <div className="grid gap-2">
                                            <div className="flex items-center">
                                                <Label htmlFor="phoneNumber">Numéro de téléphone</Label>
                                            </div>
                                            <FormField
                                                control={form.control}
                                                name="phoneNumber"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormControl>
                                                            <Input id="phoneNumber" type="tel" placeholder="Votre numéro de téléphone (70766767)" required {...field} />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                        </div>
                                        <div className="grid gap-2">
                                            <div className="flex items-center">
                                                <Label htmlFor="password">Mot de passe</Label>
                                            </div>
                                            <FormField
                                                control={form.control}
                                                name="password"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormControl>
                                                            <Input
                                                                id="password"
                                                                type="password"
                                                                required
                                                                {...field}
                                                            />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                        </div>
                                        <div className="grid gap-2">
                                            <div className="flex items-center">
                                                <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
                                            </div>
                                            <FormField
                                                control={form.control}
                                                name="confirmPassword"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormControl>
                                                            <Input
                                                                id="confirmPassword"
                                                                type="password"
                                                                required
                                                                {...field}
                                                            />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                        </div>
                                        <Button type="submit" className="w-full">
                                            {isLoading ? <Loader2 className="animate-spin" /> : "S'inscrire"}
                                        </Button>

                                    </div>
                                    <div className="mt-4 text-center text-sm">
                                        Vous avez déjà un compte ?{" "}
                                        <a href="/login" className="underline underline-offset-4">
                                            Se connecter
                                        </a>
                                    </div>
                                </form>
                            </Form>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}

export default RegisterPage;
